import type { AccountAccessToken } from '@setplex/tria-api'
import type {
  ISelectedPaymentContent,
  TryIsPurchased,
} from '~/features/payment'

import { api } from '@setplex/tria-api'
import {
  attach,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { openWindow, model as router } from '~/core/router'
import { model as session } from '~/entities/session'
import { aye, nay } from '~/shared/helpers'
import { logger } from '~/shared/logger'
import {
  getExternalPaymentDataByMediaType,
  getExternalPaymentUrlHelper,
} from './lib'
import {
  $externalPaymentBaseUrl,
  $externalPaymentEnabled,
  $selectedContent,
  handleOpenExternalPaymentPopup,
  isPurchasedRawFx,
  openExternalPaymentPopup,
} from './model'

export const giveItATry = createEvent<TryIsPurchased>()

$selectedContent.on(giveItATry, (_, { mediaType, entity, externalId }) => {
  return {
    mediaType,
    ...getExternalPaymentDataByMediaType({ mediaType, entity, externalId }),
  }
})

export const getPaymentAccountAccessTokenFx = attach({
  effect: api.subscriber.getAccountAccessTokenFx,
})

export const isPurchasedFx = attach({
  effect: isPurchasedRawFx,
  mapParams: ({
    entity,
    id,
    tvShowId,
    seasonId,
    mediaType,
    shouldRefresh,
  }: TryIsPurchased) => ({
    // assume that id is either inside of `entity` or `id`, so one is always defined
    id: entity?.id ?? id!,
    tvShowId: entity?.tvShowId ?? tvShowId,
    seasonId: entity?.seasonId ?? seasonId,
    mediaType: entity?.mediaType ?? mediaType,
    shouldRefresh,
  }),
})

const $externalPaymentAccessToken = createStore<AccountAccessToken | null>(null)

export const proceed = createEvent<ISelectedPaymentContent | undefined | void>()
export const proceedFx = createEffect((url: string | null) => {
  if (url) return openWindow(url, 'external_shopping_cart', 'noreferrer')
  throw new Error('Empty link')
})

sample({
  clock: giveItATry,
  source: $externalPaymentEnabled,
  filter: (externalPaymentEnabled) => aye(externalPaymentEnabled),
  fn: (_, params) => params,
  target: isPurchasedFx,
})

sample({
  clock: isPurchasedFx.doneData,
  source: giveItATry,
  filter: ({ link, shouldRefresh }, isPurchased) =>
    aye(link) && aye(isPurchased) && nay(shouldRefresh),
  fn: ({ link }) => link!,
  target: router.navigatePush,
})

sample({
  clock: isPurchasedFx.doneData,
  source: giveItATry,
  filter: ({ link, shouldRefresh }, isPurchased) =>
    nay(link) && aye(isPurchased) && aye(shouldRefresh),
  target: router.refresh, // not sure what to do without link, so, just refresh page just in case
})

sample({
  clock: isPurchasedFx.doneData,
  filter: (isPurchased) => nay(isPurchased),
  target: handleOpenExternalPaymentPopup,
})

sample({
  clock: openExternalPaymentPopup,
  source: $externalPaymentEnabled,
  filter: (externalPaymentEnabled) => aye(externalPaymentEnabled),
  fn: (_, params) => params,
  target: getPaymentAccountAccessTokenFx,
})

sample({
  clock: getPaymentAccountAccessTokenFx.doneData,
  target: $externalPaymentAccessToken,
})
sample({
  clock: getPaymentAccountAccessTokenFx.failData,
  fn: () => null,
  target: $externalPaymentAccessToken,
})

sample({
  clock: proceed,
  source: {
    token: $externalPaymentAccessToken,
    baseUrl: $externalPaymentBaseUrl,
    session: session.$session,
    selectedContent: $selectedContent,
  },
  fn: (source, eventSelectedContent) => {
    const {
      baseUrl,
      session,
      selectedContent: preSetSelectedContent,
      token,
    } = source

    const selectedContent = eventSelectedContent?.externalId
      ? eventSelectedContent
      : preSetSelectedContent

    return getExternalPaymentUrlHelper({
      baseUrl,
      session,
      selectedContent,
      token,
    })
  },
  target: proceedFx,
})

sample({
  clock: proceedFx.failData,
  fn: (error) => ['failed to open shopping cart window', error],
  target: logger.errorFx,
})
